import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ButtonBase from "@mui/material/ButtonBase";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobilePage from "mobile/components/MobilePage";
import { IosCard, IosFilterBar, IosSectionHeader, iosColors } from "mobile/components/IosParity";

const normalize = (d) => (d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id });
const normalizeRelation = (value) => {
  if (!value) return null;
  if (value?.data) return normalizeRelation(value.data);
  return normalize(value);
};

const getImageUrl = (image) => {
  const row = normalizeRelation(image);
  const url = row?.url || row?.formats?.thumbnail?.url || "";
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith("/") ? url : `/${url}`;
};

const money = (value) => {
  if (value === "" || value === null || value === undefined) return "-";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "-";
};

const typeLabel = (value) =>
  ({
    FG: "Finished Goods",
    BI: "Bulk Items",
    CORE: "Core",
    BANK: "Bank",
    OVERHEAD: "Overhead",
    FIXED_COSTS: "Fixed Costs",
  }[String(value || "").toUpperCase()] || String(value || "-"));

const statusTone = (value) => {
  const status = String(value || "").toLowerCase();
  if (status === "available") return "green";
  if (status === "reserved") return "orange";
  if (status === "consumed" || status === "discontinued") return "red";
  return "orange";
};

const toneColor = (tone) => (tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : "#8F3700");

const TYPE_FILTERS = [
  { key: "all", label: "All", icon: "filter_list" },
  { key: "FG", label: "Finished", icon: "inventory_2", accent: "green" },
  { key: "BI", label: "Build item", icon: "precision_manufacturing", accent: "orange" },
  { key: "CORE", label: "Core", icon: "assignment_return", accent: "red" },
  { key: "BANK", label: "Bank", icon: "account_balance", accent: "green" },
  { key: "OVERHEAD", label: "Overhead", icon: "category" },
  { key: "FIXED_COSTS", label: "Fixed costs", icon: "receipt_long" },
];

const STATUS_FILTERS = [
  { key: "all", label: "Any", icon: "tune" },
  { key: "available", label: "Available", icon: "check_circle", accent: "green" },
  { key: "reserved", label: "Reserved", icon: "lock_clock", accent: "orange" },
  { key: "consumed", label: "Consumed", icon: "remove_circle", accent: "red" },
  { key: "discontinued", label: "Discontinued", icon: "remove_circle" },
];

const BANK_FILTERS = [
  { key: "all", label: "All", icon: "account_balance" },
  { key: "bankItems", label: "Bank items", icon: "account_balance", accent: "green" },
  { key: "linkedToBank", label: "Has bank link", icon: "link", accent: "green" },
  { key: "noBankLink", label: "No bank link", icon: "link_off", accent: "red" },
];

export default function InventoryMobile() {
  const navigate = useNavigate();
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [bankFilter, setBankFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    const t = window.setTimeout(() => setSubmittedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [type, status, bankFilter, submittedSearch]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["m-inventory", type, status, bankFilter, submittedSearch, page, pageSize],
    queryFn: () =>
      get("/api/inventory-items", {
        sort: "updatedAt:desc",
        pagination: { page, pageSize },
        populate: {
          images: true,
          whiteLabelLogo: true,
          whiteLabelCustomer: true,
          coreItem: true,
          bankItem: true,
          part: true,
          transmissions: { fields: ["documentId", "name", "manufacturer", "type", "family"] },
        },
        filters: {
          ...(type !== "all" && { itemType: { $eq: type } }),
          ...(status !== "all" && { status: { $eq: status } }),
          ...(bankFilter === "bankItems" && {
            $or: [{ itemType: { $eq: "BANK" } }, { sku: { $containsi: "-BANK" } }],
          }),
          ...(bankFilter === "linkedToBank" && { bankItem: { id: { $notNull: true } } }),
          ...(bankFilter === "noBankLink" && { bankItem: { id: { $null: true } } }),
          ...(submittedSearch && {
            $or: [
              { sku: { $containsi: submittedSearch } },
              { name: { $containsi: submittedSearch } },
              { location: { $containsi: submittedSearch } },
              { lotNumber: { $containsi: submittedSearch } },
              { serialNumber: { $containsi: submittedSearch } },
              { coreItem: { sku: { $containsi: submittedSearch } } },
              { bankItem: { sku: { $containsi: submittedSearch } } },
            ],
          }),
        },
      }),
    staleTime: 5_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <MobilePage
      title="Inventory"
      subtitle={
        typeof total === "number"
          ? `${total} total • page ${page}/${pageCount}`
          : rows.length
          ? `${rows.length} shown`
          : " "
      }
      right={isFetching ? <Chip size="small" label="Syncing..." variant="outlined" /> : null}
      onBack={() => navigate("/m/dashboard")}
    >
      <Box sx={{ mt: 1 }}>
        <IosSectionHeader eyebrow="Inventory" title="Lookup + stock" subtitle="Search the same SKU, core, bank, lot, serial, and location fields used on web." />
      </Box>
      <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: iosColors.canvas, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search SKU, name, core, bank, lot, serial, location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSubmittedSearch(search.trim());
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: iosColors.subtle }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  sx={{ color: "#8F3700", bgcolor: "rgba(255,106,0,0.07)", "&:hover": { bgcolor: "rgba(255,106,0,0.12)" } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <IosFilterBar label="Type" value={type} options={TYPE_FILTERS} onChange={setType} />
        <IosFilterBar label="Availability" value={status} options={STATUS_FILTERS} onChange={setStatus} />
        <IosFilterBar label="Bank" value={bankFilter} options={BANK_FILTERS} onChange={setBankFilter} />
      </Box>

      {isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ px: 0.5, py: 1 }}>
          Failed to load inventory.
        </Typography>
      ) : null}

      <Stack spacing={0.75}>
        {rows.map((item) => {
          const documentId = item.documentId;
          const qty = Number(item.quantityOnHand ?? 0);
          const reserved = Number(item.quantityReserved ?? 0);
          const statusLabel = String(item.status || "available");
          const coreItem = normalizeRelation(item.coreItem);
          const bankItem = normalizeRelation(item.bankItem);
          const primaryImage = item.images?.data?.[0] || item.images?.[0] || item.whiteLabelLogo?.data || item.whiteLabelLogo;
          const imageUrl = getImageUrl(primaryImage);
          const whiteLabelCustomer = normalizeRelation(item.whiteLabelCustomer);
          const retailPrice = item.priceLevels?.retail ?? item.unitPrice;

          return (
            <ButtonBase
              key={documentId || item.id}
              onClick={() => {
                if (documentId) navigate(`/m/inventory/${documentId}`);
              }}
              sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}
            >
              <IosCard sx={{ width: "100%", p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Avatar
                    src={imageUrl || undefined}
                    variant="rounded"
                    sx={{ width: 46, height: 46, bgcolor: "rgba(255,106,0,0.08)", color: "#8F3700", borderRadius: 1, flexShrink: 0 }}
                  >
                    <Icon>inventory_2</Icon>
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.55} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: iosColors.ink, fontSize: 15.7, fontWeight: 900, lineHeight: 1.18 }}>
                          {item.sku || "Inventory item"}
                        </Typography>
                        <Typography sx={{ color: iosColors.muted, fontSize: 12.8, lineHeight: 1.28, mt: 0.25 }}>
                          {item.name || "No name"}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: toneColor(statusTone(statusLabel)), fontSize: 12.2, fontWeight: 900, textAlign: "right", flexShrink: 0 }}>
                        QOH {Number.isFinite(qty) ? qty : 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap sx={{ mt: 0.65 }}>
                      <Chip size="small" label={typeLabel(item.itemType)} sx={{ bgcolor: "rgba(255,106,0,0.07)", color: "#8F3700", border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
                      <Chip size="small" label={statusLabel} sx={{ bgcolor: "#fff", color: toneColor(statusTone(statusLabel)), border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
                      {item.location ? <Chip size="small" label={item.location} sx={{ bgcolor: "#fff", color: iosColors.muted, border: `1px solid ${iosColors.line}`, fontWeight: 850 }} /> : null}
                      {reserved ? <Chip size="small" label={`Reserved ${reserved}`} sx={{ bgcolor: "#fff", color: "#8F3700", border: `1px solid ${iosColors.line}`, fontWeight: 850 }} /> : null}
                    </Stack>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.35, lineHeight: 1.3, mt: 0.65 }}>
                      {[coreItem?.sku ? `Core ${coreItem.sku}` : null, bankItem?.sku ? `Bank ${bankItem.sku}` : null, retailPrice != null ? `Retail ${money(retailPrice)}` : null]
                        .filter(Boolean)
                        .join(" • ") || "No linked core/bank details"}
                    </Typography>
                    {item.isWhiteLabel || whiteLabelCustomer ? (
                      <Typography sx={{ color: iosColors.green, fontSize: 12.1, fontWeight: 850, mt: 0.45 }}>
                        White label: {item.whiteLabelName || whiteLabelCustomer?.name || "Configured"}
                      </Typography>
                    ) : null}
                  </Box>
                  <Icon sx={{ color: iosColors.subtle, fontSize: 19, mt: 0.15 }}>chevron_right</Icon>
                </Stack>
              </IosCard>
            </ButtonBase>
          );
        })}

        {!isLoading && !rows.length ? (
          <IosCard>
            <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No inventory items match this view.</Typography>
          </IosCard>
        ) : null}
      </Stack>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(e, p) => setPage(p)}
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 900,
                borderRadius: 1,
              },
              "& .Mui-selected": {
                bgcolor: "#B54708 !important",
                color: "#fff",
              },
            }}
          />
        </Box>
      ) : null}
    </MobilePage>
  );
}
